/**
 * QuickBooks Journal CSV parsing — pure, dependency-free, shared by the
 * Worker importer (api/accounting/import-journal-csv.ts) and the client-side
 * pre-import preflight (utils/qb-preflight.ts). One parser, two consumers —
 * keep ALL journal-export shape knowledge here so the preflight can never
 * disagree with what the importer would actually post.
 *
 * QB Journal export format (verified against real exports):
 *   Header: "Trans #","Type","Date","Num","Name","Memo","Account","Debit","Credit"
 *   Each transaction is N+1 rows:
 *     - First row carries Trans #, Type, Date, Num + first line (Name/Memo/Account/Debit/Credit)
 *     - Subsequent line rows have empty leading columns + remaining line fields
 *     - Terminator row has empties everywhere except Debit + Credit totals
 *   Account cell format: "<code> · <name>" (UTF-8 middle-dot, often
 *   CP1252-encoded; we tolerate both).
 */

export interface ParsedLine {
	name: string | null;
	memo: string | null;
	accountCode: string;
	accountName: string;
	debitCents: number;
	creditCents: number;
}

export interface ParsedEntry {
	transactionId: string;       // QB "Trans #"
	type: string;                // raw QB Type
	postedOn: number;            // UTC midnight ms
	num: string | null;          // QB "Num" (check #, invoice #, etc.)
	lines: ParsedLine[];
}

// ────────────────────────────────────────────────────────────────────────────
// RFC4180-ish CSV parser. QB exports use CRLF, quoted fields with embedded
// commas, and `""` for literal quotes. We avoid pulling a CSV dep into the
// Worker bundle.
// ────────────────────────────────────────────────────────────────────────────
export function parseCsv(text: string): string[][] {
	const rows: string[][] = [];
	let row: string[] = [];
	let field = '';
	let inQuotes = false;
	for (let i = 0; i < text.length; i++) {
		const ch = text[i];
		if (inQuotes) {
			if (ch === '"') {
				if (text[i + 1] === '"') {
					field += '"';
					i++;
				} else {
					inQuotes = false;
				}
			} else {
				field += ch;
			}
			continue;
		}
		if (ch === '"') {
			inQuotes = true;
		} else if (ch === ',') {
			row.push(field);
			field = '';
		} else if (ch === '\r') {
			// swallow; CRLF handled by \n branch
		} else if (ch === '\n') {
			row.push(field);
			field = '';
			rows.push(row);
			row = [];
		} else {
			field += ch;
		}
	}
	if (field.length > 0 || row.length > 0) {
		row.push(field);
		rows.push(row);
	}
	return rows;
}

// QB encodes the bullet/middle-dot as CP1252 0xB7 → arrives as the UTF-8
// replacement character (·) or as `�` depending on the upload encoding.
// Anything non-alphanumeric between the code and name is the separator.
export function parseAccountCell(cell: string): { code: string; name: string } {
	const s = cell.trim();
	if (!s) return { code: '', name: '' };
	// Code is the leading run of digits/dots (e.g. "7060.42").
	const m = s.match(/^([0-9][0-9.]*)\s*[^A-Za-z0-9]+\s*(.*)$/);
	if (m) return { code: m[1], name: m[2].trim() };
	// No code — return whole string as name.
	return { code: '', name: s };
}

export function parseMoneyToCents(s: string): number {
	if (!s) return 0;
	const cleaned = s.replace(/[",$\s]/g, '');
	if (!cleaned) return 0;
	const n = Number(cleaned);
	if (!Number.isFinite(n)) return 0;
	return Math.round(n * 100);
}

// QB dates: "MM/DD/YYYY" → UTC midnight ms (business-date convention per
// memory feedback_business_date_utc_getters).
export function parseQbDate(s: string): number | null {
	if (!s) return null;
	const m = s.trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
	if (!m) return null;
	const month = Number(m[1]);
	const day = Number(m[2]);
	const year = Number(m[3]);
	return Date.UTC(year, month - 1, day);
}

/**
 * Walk the parsed rows, group into entries. Each entry starts on a row
 * with a non-empty Trans #; lines append until a terminator row (all empty
 * except Debit + Credit totals). The terminator row itself is dropped.
 */
export function parseJournalRows(rows: string[][]): { entries: ParsedEntry[]; errors: string[] } {
	const entries: ParsedEntry[] = [];
	const errors: string[] = [];
	if (rows.length === 0) return { entries, errors: ['Empty CSV'] };

	// Validate header
	const header = rows[0].map((c) => c.trim().toLowerCase());
	const expected = ['trans #', 'type', 'date', 'num', 'name', 'memo', 'account', 'debit', 'credit'];
	const headerOk = expected.every((h, i) => header[i] === h);
	if (!headerOk) {
		errors.push(`Unexpected header: ${rows[0].join(',')}`);
		return { entries, errors };
	}

	let current: ParsedEntry | null = null;
	for (let i = 1; i < rows.length; i++) {
		const r = rows[i];
		if (r.every((c) => c.trim() === '')) continue;

		const transId = (r[0] ?? '').trim();
		const type = (r[1] ?? '').trim();
		const date = (r[2] ?? '').trim();
		const num = (r[3] ?? '').trim();
		const name = (r[4] ?? '').trim();
		const memo = (r[5] ?? '').trim();
		const acctCell = (r[6] ?? '').trim();
		const debitStr = (r[7] ?? '').trim();
		const creditStr = (r[8] ?? '').trim();

		// Terminator row: only Debit + Credit set, all the rest blank.
		// Push the in-progress entry before clearing — otherwise every entry
		// followed by a terminator (i.e., every entry except the last) gets
		// silently dropped.
		if (!transId && !type && !date && !num && !name && !memo && !acctCell) {
			if (current) {
				entries.push(current);
				current = null;
			}
			continue;
		}

		// New entry header row.
		if (transId) {
			// QB Journal exports end with a grand-total footer row whose first
			// column is the literal "TOTAL" and which has no date/type/account.
			// Treat that as end-of-report rather than a malformed entry.
			if (/^TOTAL$/i.test(transId) && !type && !date && !acctCell) {
				if (current) {
					entries.push(current);
					current = null;
				}
				continue;
			}
			if (current) {
				entries.push(current);
			}
			const postedOn = parseQbDate(date);
			if (postedOn === null) {
				errors.push(`Trans #${transId}: unparseable date "${date}"`);
				current = null;
				continue;
			}
			current = {
				transactionId: transId,
				type,
				postedOn,
				num: num || null,
				lines: [],
			};
		}
		if (!current) continue;
		if (!acctCell) continue; // header row with no line yet? rare; skip

		const { code, name: acctName } = parseAccountCell(acctCell);
		current.lines.push({
			name: name || null,
			memo: memo || null,
			accountCode: code,
			accountName: acctName,
			debitCents: parseMoneyToCents(debitStr),
			creditCents: parseMoneyToCents(creditStr),
		});
	}
	if (current) entries.push(current);
	return { entries, errors };
}
